import { ProjectArchive } from './ProjectArchive.ts'
import { type SkeletonDescriptor } from '../skeleton/SkeletonDescriptor.ts'
import { ProjectSessionStorage, generate_session_id } from './ProjectSessionStorage.ts'
import { ModalDialog } from '../../lib/ModalDialog.ts'
import { escape_html } from '../../lib/html.ts'

/**
 * Host interface that ProjectImporter calls back into. Keeps the importer
 * decoupled from StepAnimationsListing — anything that holds a clip list and
 * can refresh its UI can be a host.
 */
export interface ProjectImportHost {
  /** Drop all user-authored clips from in-memory state (storage is separately overwritten by apply()). */
  drop_user_clips_in_memory: () => void
  /** Refresh in-memory user clips from IDB. */
  restore_user_clips_async: () => Promise<void>
  /** Notify the host that the clip list has changed and the UI should rebuild. */
  on_clips_changed: () => void
}

export interface ProjectImportPreviewSummary {
  version_label: string
  skeleton_count: number
  clip_count: number
  custom_skeleton_count: number
  settings_count: number
  has_mesh: boolean
  mesh_kb?: number
  skeleton_labels: string[]
}

async function detect_zip (file: File): Promise<boolean> {
  if (file.size < 4) return false
  const head = new Uint8Array(await file.slice(0, 4).arrayBuffer())
  return head[0] === 0x50 && head[1] === 0x4b && head[2] === 0x03 && head[3] === 0x04
}

export function create_project_import_preview_summary (input: {
  version_label: string
  skeletons: Record<string, { clips: unknown[] }>
  custom_skeleton_count: number
  settings_count: number
  has_mesh: boolean
  mesh_kb?: number
}): ProjectImportPreviewSummary {
  const skeleton_entries = Object.entries(input.skeletons)
  return {
    version_label: input.version_label,
    skeleton_count: skeleton_entries.length,
    clip_count: skeleton_entries.reduce((sum, [, entry]) => sum + entry.clips.length, 0),
    custom_skeleton_count: input.custom_skeleton_count,
    settings_count: input.settings_count,
    has_mesh: input.has_mesh,
    mesh_kb: input.mesh_kb,
    skeleton_labels: skeleton_entries
      .map(([name, entry]) => `${name}: ${entry.clips.length}`)
      .sort((a, b) => a.localeCompare(b))
  }
}

export function build_project_import_preview_html (summary: ProjectImportPreviewSummary): string {
  const mesh_text = summary.has_mesh
    ? `Bundled rigged mesh: ${summary.mesh_kb === undefined ? 'included' : `${summary.mesh_kb.toFixed(1)} KB`}`
    : 'Bundled rigged mesh: none'
  const skeleton_rows = summary.skeleton_labels.length > 0
    ? summary.skeleton_labels.map(label => `<li>${escape_html(label)} clip(s)</li>`).join('')
    : '<li>No saved clips in this archive.</li>'

  return `
    <div class="project-import-preview">
      <p>This import replaces saved clips and supported settings.</p>
      <ul>
        <li>Archive type: ${escape_html(summary.version_label)}</li>
        <li>Saved clips: ${summary.clip_count} across ${summary.skeleton_count} rig type(s)</li>
        <li>Custom skeletons: ${summary.custom_skeleton_count}</li>
        <li>Settings: ${summary.settings_count}</li>
        <li>${escape_html(mesh_text)}</li>
      </ul>
      <h3>Clip Groups</h3>
      <ul>${skeleton_rows}</ul>
      <div class="project-import-preview-actions">
        <button id="project-import-confirm-button">Import</button>
        <button id="project-import-cancel-button" class="secondary-button">Cancel</button>
      </div>
    </div>
  `
}

function confirm_project_import (summary: ProjectImportPreviewSummary): Promise<boolean> {
  return new Promise((resolve) => {
    let resolved = false
    const finish = (value: boolean, dialog: ModalDialog): void => {
      if (resolved) return
      resolved = true
      dialog.remove()
      resolve(value)
    }
    const dialog = new ModalDialog('Import project', build_project_import_preview_html(summary), {
      customClass: 'project-import-preview-dialog',
      onClose: () => {
        if (!resolved) {
          resolved = true
          resolve(false)
        }
      }
    })
    dialog.show()
    document.querySelector<HTMLButtonElement>('#project-import-confirm-button')?.addEventListener('click', () => finish(true, dialog))
    document.querySelector<HTMLButtonElement>('#project-import-cancel-button')?.addEventListener('click', () => finish(false, dialog))
  })
}

function merge_v2_custom_skeletons (
  manifest_skeletons: SkeletonDescriptor[] | undefined,
  descriptor: SkeletonDescriptor | null
): SkeletonDescriptor[] {
  const custom_skeletons = [...(manifest_skeletons ?? [])]
  if (descriptor !== null && !custom_skeletons.some(skeleton => skeleton.id === descriptor.id)) {
    custom_skeletons.push(descriptor)
  }
  return custom_skeletons
}

async function save_v2_mesh_as_project_session (
  file_name: string,
  mesh_bytes: Uint8Array,
  descriptor: SkeletonDescriptor | null
): Promise<string> {
  const base_name = file_name.replace(/\.meshinator\.zip$|\.zip$/i, '')
  const now = new Date().toISOString()
  const session_id = generate_session_id()
  const bytes = new ArrayBuffer(mesh_bytes.byteLength)
  new Uint8Array(bytes).set(mesh_bytes)
  const saved = await ProjectSessionStorage.put({
    id: session_id,
    name: base_name,
    created_at: now,
    updated_at: now,
    last_step: 'load-skeleton',
    model: {
      bytes,
      extension: 'glb',
      file_name: `${base_name}.glb`
    },
    skeleton_selection: descriptor === null ? '' : `custom:${descriptor.id}`
  })
  if (!saved) throw new Error('Could not save the bundled mesh for project restore.')
  return session_id
}

export async function import_project_file (file: File, host: ProjectImportHost): Promise<void> {
  const is_zip = await detect_zip(file)
  if (is_zip) {
    await import_v2_zip(file, host)
  } else {
    await import_v1_json(file, host)
  }
}

async function import_v1_json (file: File, host: ProjectImportHost): Promise<void> {
  let text: string
  try {
    text = await file.text()
  } catch (err) {
    new ModalDialog('Import failed', `Couldn't read the file: ${(err as Error).message}`).show()
    return
  }

  let project: ReturnType<typeof ProjectArchive.parse>
  try {
    project = ProjectArchive.parse(text)
  } catch (err) {
    new ModalDialog('Import failed', (err as Error).message).show()
    return
  }

  const summary = create_project_import_preview_summary({
    version_label: 'v1 JSON',
    skeletons: project.skeletons,
    custom_skeleton_count: project.custom_skeletons?.length ?? 0,
    settings_count: Object.keys(project.settings).length,
    has_mesh: false
  })
  const ok = await confirm_project_import(summary)
  if (!ok) return

  try {
    await ProjectArchive.apply(project)
  } catch (err) {
    new ModalDialog('Import failed', `Failed to apply project: ${(err as Error).message}`).show()
    return
  }

  host.drop_user_clips_in_memory()
  await host.restore_user_clips_async()
  host.on_clips_changed()

  new ModalDialog('Import complete', `Restored ${summary.clip_count} clip(s). Switch skeleton types to see clips for other rigs.`).show()
}

async function import_v2_zip (file: File, host: ProjectImportHost): Promise<void> {
  let parsed: Awaited<ReturnType<typeof ProjectArchive.parse_zip>>
  try {
    const buffer = await file.arrayBuffer()
    parsed = await ProjectArchive.parse_zip(buffer)
  } catch (err) {
    new ModalDialog('Import failed', `Couldn't read the v2 zip: ${(err as Error).message}`).show()
    return
  }

  const custom_skeletons = merge_v2_custom_skeletons(parsed.manifest.custom_skeletons, parsed.descriptor)
  const custom_skel_count = custom_skeletons.length
  const has_mesh = parsed.mesh_bytes !== null
  const summary = create_project_import_preview_summary({
    version_label: 'v2 ZIP',
    skeletons: parsed.manifest.skeletons,
    custom_skeleton_count: custom_skel_count,
    settings_count: Object.keys(parsed.manifest.settings).length,
    has_mesh,
    mesh_kb: has_mesh ? parsed.mesh_bytes!.length / 1024 : undefined
  })
  const ok = await confirm_project_import(summary)
  if (!ok) return

  // The v2 manifest is a superset of v1 — feed it to apply() via a v1 view.
  const v1_view = {
    format: 'meshinator-project' as const,
    version: 1 as const,
    exported_at: parsed.manifest.exported_at,
    skeletons: parsed.manifest.skeletons,
    settings: parsed.manifest.settings,
    custom_skeletons
  }
  try {
    await ProjectArchive.apply(v1_view)
  } catch (err) {
    new ModalDialog('Import failed', `Failed to apply v2 project: ${(err as Error).message}`).show()
    return
  }

  if (has_mesh) {
    const session_id = await save_v2_mesh_as_project_session(file.name, parsed.mesh_bytes!, parsed.descriptor)
    window.dispatchEvent(new CustomEvent('meshinator-project-session-imported', { detail: { session_id } }))
  }

  host.drop_user_clips_in_memory()
  await host.restore_user_clips_async()
  host.on_clips_changed()

  new ModalDialog(
    'Import complete (v2)',
    `Restored ${summary.clip_count} clip(s) and ${custom_skel_count} custom skeleton(s).`
    + (has_mesh ? ' The rigged mesh was added to Recent Projects.' : '')
  ).show()
}
